import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import Button from "../../components/common/Button";
import { useAuth } from '../../hooks/useAuth';
import Input from "../../components/common/Input";
import ListLoadError from "../../components/admin/ListLoadError";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Modal from "../../components/common/Modal";
import api from "../../services/api";
import styles from "./AgenciesPage.module.css";

const ENTITY_TYPE_LABELS = {
  agency: "Agency",
  agent: "Agent",
  tpa: "TPA"
};

const AgenciesPage = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('agencies.edit');
  const canCreate = hasPermission('agencies.create');
  const canDelete = hasPermission('agencies.delete');
  const [editingAgency, setEditingAgency] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isActive: true,
    entityType: "agency"
  });

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async (searchTerm = "", page = 1) => {
    try {
      setLoading(true);
      setLoadError(false);
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      params.append("page", page);
      params.append("limit", 50);

      const response = await api.get(`/admin/agencies?${params.toString()}`);
      setAgencies(response.agencies || []);
      setPagination(response.pagination || null);
    } catch (error) {
      setLoadError(true);
      setAgencies([]);
      toast.error("Failed to load entities");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadAgencies(search);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const openCreateModal = () => {
    setEditingAgency(null);
    setFormData({ name: "", description: "", isActive: true, entityType: "agency" });
    setModalOpen(true);
  };

  const openEditModal = (agency) => {
    setEditingAgency(agency);
    setFormData({
      name: agency.name || "",
      description: agency.description || "",
      isActive: agency.isActive !== false,
      entityType: agency.entityType || "agency"
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingAgency(null);
    setFormData({ name: "", description: "", isActive: true, entityType: "agency" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Entity name is required");
      return;
    }

    try {
      setSaving(true);
      if (editingAgency) {
        await api.patch(`/admin/agencies/${editingAgency.id}`, formData);
        toast.success("Entity updated successfully");
      } else {
        await api.post("/admin/agencies", formData);
        toast.success("Entity created successfully");
      }
      closeModal();
      loadAgencies(search);
    } catch (error) {
      toast.error(error.message || "Failed to save entity");
    } finally {
      setSaving(false);
    }
  };

  const handleViewDetails = (agency) => {
    navigate(`/admin/agencies/${agency.id}`);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Entities</h1>
          <p>Manage entities and view their client metrics</p>
        </div>
        {canCreate && <Button onClick={openCreateModal}>Add Entity</Button>}
      </div>

      {/* Search */}
      <div className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <Input
            placeholder="Search entities..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
          <Button type="submit" variant="secondary">Search</Button>
        </form>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : loadError ? (
          <ListLoadError
            message="Could not load entities"
            onRetry={() => loadAgencies(search)}
          />
        ) : agencies.length === 0 ? (
          <div className={styles.empty}>
            <p>No entities yet</p>
            {canCreate && <Button onClick={openCreateModal} variant="secondary">Create First Entity</Button>}
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Entity Type</th>
                <th>Created</th>
                <th>Clients</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {agencies.map((agency) => (
                <tr key={agency.id}>
                  <td className={styles.nameCell}>{agency.name}</td>
                  <td>
                    <span className={styles.typeBadge}>
                      {ENTITY_TYPE_LABELS[agency.entityType] || "Agency"}
                    </span>
                  </td>
                  <td>{formatDate(agency.createdAt)}</td>
                  <td>{agency.memberCount ?? "-"}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${agency.isActive ? styles.active : styles.inactive}`}>
                      {agency.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className={styles.actions}>
                    <button
                      className={styles.actionBtn}
                      onClick={() => openEditModal(agency)} style={!canEdit ? {display:'none'} : {}}
                      title="Edit"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className={styles.actionBtn}
                      onClick={() => handleViewDetails(agency)}
                      title="View Details"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} onClose={closeModal} title={editingAgency ? "Edit Entity" : "Create Entity"}>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label>Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Entity name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label>Entity Type</label>
            <select
              value={formData.entityType}
              onChange={(e) => setFormData({ ...formData, entityType: e.target.value })}
              className={styles.select}
            >
              <option value="agency">Agency</option>
              <option value="agent">Agent</option>
              <option value="tpa">TPA</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Status</label>
            <select
              value={formData.isActive ? "active" : "inactive"}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.value === "active" })}
              className={styles.select}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className={styles.formGroup}>
            <label>Notes</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Optional notes about this agency..."
              className={styles.textarea}
              rows={4}
            />
          </div>

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={closeModal}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : (editingAgency ? "Update Entity" : "Create Entity")}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AgenciesPage;
